import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Database, Server, Mail, Phone, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Clock, RefreshCw,
  Trash2, RotateCcw, Activity, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch health:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/super-admin/jobs/${jobId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Job queued for retry');
      fetchHealth();
    } catch (error) {
      toast.error('Failed to retry job');
    }
  };

  const handleRetryAll = async () => {
    setRetrying(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/jobs/retry-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.retried} jobs queued for retry`);
      fetchHealth();
    } catch (error) {
      toast.error('Failed to retry jobs');
    } finally {
      setRetrying(false);
    }
  };

  const getServiceIcon = (service) => {
    const icons = {
      mongodb: Database,
      scheduler: Clock,
      resend_email: Mail,
      twilio_sms: Phone,
      vapi_voice: MessageSquare
    };
    return icons[service] || Server;
  };

  const getServiceLabel = (service) => {
    const labels = {
      mongodb: 'MongoDB',
      scheduler: 'Job Scheduler',
      resend_email: 'Resend Email',
      twilio_sms: 'Twilio SMS',
      vapi_voice: 'Vapi Voice AI'
    };
    return labels[service] || service;
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  const services = data?.services || {};
  const jobs = data?.jobs || {};
  const healthyCount = Object.values(services).filter(s => s.healthy).length;
  const totalServices = Object.keys(services).length;

  return (
    <div className="min-h-screen bg-[#0a0f1e] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-2 text-[#94a3b8] hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-white">Platform Health</h1>
          <p className="text-[#94a3b8]">Monitor services, jobs, and system status</p>
        </div>
        <Button
          onClick={fetchHealth}
          variant="outline"
          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={`mb-8 ${healthyCount === totalServices ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            {healthyCount === totalServices ? (
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            ) : (
              <AlertCircle className="w-12 h-12 text-amber-400" />
            )}
            <div>
              <h2 className="text-2xl font-bold text-white">
                {healthyCount === totalServices ? 'All Systems Operational' : 'Some Services Need Attention'}
              </h2>
              <p className="text-[#94a3b8]">{healthyCount}/{totalServices} services healthy</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Services Status */}
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-400" />
              Service Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(services).map(([key, service]) => {
                const Icon = getServiceIcon(key);
                return (
                  <div key={key} className="flex items-center justify-between p-4 bg-[#0a0f1e] rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${service.healthy ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                        <Icon className={`w-5 h-5 ${service.healthy ? 'text-emerald-400' : 'text-red-400'}`} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{getServiceLabel(key)}</p>
                        <p className="text-xs text-[#94a3b8] capitalize">{service.status}</p>
                      </div>
                    </div>
                    {service.healthy ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Healthy
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400">
                        <XCircle className="w-3 h-3 mr-1" />
                        Issue
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Jobs Queue */}
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Jobs Queue
              </CardTitle>
              {jobs.failed > 0 && (
                <Button
                  onClick={handleRetryAll}
                  disabled={retrying}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {retrying ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4 mr-1" />
                  )}
                  Retry All Failed
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-[#94a3b8] text-sm">Pending</span>
                </div>
                <p className="text-2xl font-bold text-white">{jobs.pending || 0}</p>
              </div>
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-amber-400" />
                  <span className="text-[#94a3b8] text-sm">Processing</span>
                </div>
                <p className="text-2xl font-bold text-white">{jobs.processing || 0}</p>
              </div>
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-[#94a3b8] text-sm">Failed</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{jobs.failed || 0}</p>
              </div>
              <div className="p-4 bg-[#0a0f1e] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-[#94a3b8] text-sm">Today</span>
                </div>
                <p className="text-2xl font-bold text-emerald-400">{jobs.completed_today || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed Jobs List */}
      {jobs.failed_list?.length > 0 && (
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Failed Jobs ({jobs.failed_list.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1e3a5f30]">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Lead</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Error</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Time</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.failed_list.map((job, index) => (
                    <tr key={index} className="border-b border-[#1e3a5f30]">
                      <td className="py-3 px-4 text-white">{job.lead_name || 'Unknown'}</td>
                      <td className="py-3 px-4">
                        <Badge className="bg-gray-500/20 text-gray-300">{job.type}</Badge>
                      </td>
                      <td className="py-3 px-4 text-red-400 text-sm max-w-xs truncate">
                        {job.error || 'Unknown error'}
                      </td>
                      <td className="py-3 px-4 text-[#94a3b8] text-sm">
                        {formatDate(job.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleRetryJob(job._id)}
                          className="p-2 hover:bg-[#1e3a5f] rounded-lg text-blue-400"
                          title="Retry"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Info */}
      <Card className="mt-6 bg-[#1a2235] border-[#1e3a5f30]">
        <CardHeader>
          <CardTitle className="text-white">System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#0a0f1e] rounded-xl">
              <p className="text-[#94a3b8] text-sm">API Version</p>
              <p className="text-white font-medium">2.1.1</p>
            </div>
            <div className="p-4 bg-[#0a0f1e] rounded-xl">
              <p className="text-[#94a3b8] text-sm">Environment</p>
              <p className="text-white font-medium">Production</p>
            </div>
            <div className="p-4 bg-[#0a0f1e] rounded-xl">
              <p className="text-[#94a3b8] text-sm">Last Check</p>
              <p className="text-white font-medium">{formatDate(data?.timestamp)}</p>
            </div>
            <div className="p-4 bg-[#0a0f1e] rounded-xl">
              <p className="text-[#94a3b8] text-sm">Uptime</p>
              <p className="text-emerald-400 font-medium">99.9%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminHealth;
